import { H1 } from '@berlitz/globals'
import { Tick } from '@berlitz/icons'
import Modal from '@berlitzplatforms/micro.ui.modal'
import Spacer from '@berlitz/spacer'
import Spinner from '@berlitz/spinner'
import { Attachment } from '@interfaces/ScheduleSession'
import { IconCircle, Success } from '@layouts/LessonRegistration/AddNoteModal/style'
import { uploadFileProxy, getFileProxyBaseUrl } from '@utils/aws-api'
import { getFileExtension } from '@utils/helpers'
import { getIcon } from '@utils/helpers/fileicons'
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'
import HR from '../HR'
import { CenterText, SpinnerWrapper } from '../Modal/style'
import Dropfile from './Dropfile'
import FilenameWithIcon from './FileContainer'
import { FileuploadProp } from './interface'
import { ButtonContainer, StyledButton, TitleWrapper, Wrapper } from './style'
import { PORTAL_ACCEPTED_FILE_TYPES } from '@utils/constants'

interface FileUploadState {
  files: File[]
  uploading: boolean
  uploaded: boolean
  uploadedFiles: File[]
  canOpenFiles: boolean
}

class Fileupload extends React.PureComponent<FileuploadProp & InjectedIntlProps, FileUploadState> {
  static defaultProps = {
    onSave: () => {},
  }
  state = {
    files: [] as File[],
    uploadedFiles: [] as File[],
    uploading: false,
    uploaded: false,
  }

  async viewS3File(file) {
    const fileUrl = file
    // @ts-ignore
    window.open(fileUrl, '_blank')
  }

  formStateFileData = () => {
    return this.state.uploadedFiles.map((file) => ({
      // @ts-ignore
      FileUrl: file.url,
      Title: file.name,
    }))
  }

  componentDidUpdate(prevProps) {
    const { uploadedFiles } = this.state

    if (!isEqual(prevProps.files, this.props.files)) {
      const filteredFiles = uploadedFiles.filter((file) => {
        return !(
          this.props.files &&
          this.props.files.find((propFile) => {
            // @ts-ignore
            return propFile.FileUrl === file.url
          })
        )
      })
      this.setState({
        uploadedFiles: filteredFiles,
      })
    }
  }

  renderParentLoading = () => {
    return (
      <>
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
        <CenterText>
          <H1 disableMargin>
            <FormattedMessage id={this.props.parentLoadingText || 'Processing'} />
          </H1>
        </CenterText>
      </>
    )
  }

  renderParentSuccess = () => {
    return (
      <Success>
        <IconCircle color="error">
          <Tick size="lg" />
        </IconCircle>
        <H1 disableMargin>
          <FormattedMessage id={this.props.parentLoadingTextSuccess || 'Success'} />
        </H1>
      </Success>
    )
  }

  render() {
    const { uploaded } = this.state
    const { open, files, onDelete, parentLoading, parentSuccess, hidden, canOpenFiles } = this.props

    return (
      <Modal open={open} onClose={this.handleClose}>
        <Wrapper>
          {!uploaded && !parentLoading && this.renderDropFile()}
          {parentLoading && this.renderParentLoading()}
          {parentSuccess && uploaded && this.renderParentSuccess()}
          {
            // @ts-ignore
            this.renderPropsFiles('Files saved', files, hidden, onDelete, canOpenFiles)
          }
          {uploaded && this.renderUploadedFile(canOpenFiles)}
        </Wrapper>
      </Modal>
    )
  }

  private handleClose = () => {
    this.setState(
      {
        files: [],
        uploadedFiles: [],
        uploading: false,
        uploaded: false,
      },
      () => this.props.onClose()
    )
  }

  private handleSave = async () => {
    try {
      const { files, uploadedFiles } = this.state

      this.setState({
        uploading: true,
      })

      // const response = await uploadFile(files, this.props.accessLevel)
      const response = await uploadFileProxy(files)

      for (let i = 0; i < response.length; i++) {
        // @ts-ignore
        // response[i].url = await getFileBaseUrl(response[i].path, this.props.accessLevel)
        response[i].url = await getFileProxyBaseUrl(response[i].path)
      }

      await this.props.onSave(response)

      this.setState({
        uploaded: true,
        uploading: false,
        // @ts-ignore
        uploadedFiles: uploadedFiles.concat(response),
      })

      this.props.closeOnSave && this.handleClose()
    } catch (e) {
      console.log(e)
      this.setState({
        uploading: false,
      })
      // silent for now but this should be handled properly
    }
  }

  private onDrop = (files: File[]) => {
    this.setState({ files })
  }

  private renderDropFile = () => {
    const { files, uploading } = this.state
    const {
      modalTitle,
      intl,
      saveText = 'Save',
      accepted = PORTAL_ACCEPTED_FILE_TYPES,
      acceptedText = '',
      hidden,
    } = this.props
    const disabled = !files.length || uploading

    if (hidden) {
      return null
    }

    return (
      <>
        <TitleWrapper>{modalTitle ? modalTitle : <FormattedMessage id="File upload" />}</TitleWrapper>
        <Dropfile disabled={uploading} onDrop={this.onDrop} accepted={accepted} acceptedText={acceptedText} />
        <ButtonContainer>
          <StyledButton onClick={this.handleClose}>
            <FormattedMessage id="Cancel" />
          </StyledButton>
          <StyledButton
            disabled={disabled}
            onClick={this.handleSave}
            canLoad
            loading={uploading ? 1 : 0}
            loadingText={intl.formatMessage({ id: 'Uploading' })}
          >
            <FormattedMessage id={saveText} />
          </StyledButton>
        </ButtonContainer>
      </>
    )
  }

  private renderPropsFilesRow = (files, onDelete, canOpenFiles = false) => {
    return (
      <>
        {files.map((file: Attachment, index: number) => {
          const type = getFileExtension(file.Title)
          const { Icon } = getIcon(type)

          return (
            <FilenameWithIcon
              key={index}
              name={file.Title ?? ''}
              onClose={typeof onDelete === 'function' ? () => onDelete(file) : undefined}
              onClick={() => {
                if (canOpenFiles) this.viewS3File(file.FileUrl)
              }}
            >
              <Icon size="sm" />
            </FilenameWithIcon>
          )
        })}
      </>
    )
  }

  private renderUploadedFile = (canOpenFiles?: boolean) => {
    return this.renderPropsFiles('Uploaded files', this.formStateFileData(), undefined, undefined, canOpenFiles)
  }

  private renderPropsFiles = (
    title: string,
    files: any,
    hidden?: boolean,
    onDelete?: () => {},
    canOpenFiles?: boolean
  ) => {
    return files && !!files.length ? (
      <>
        {!hidden && <Spacer />}
        {!hidden && <HR />}
        <TitleWrapper>
          <FormattedMessage id={title} />
        </TitleWrapper>
        <Row>
          <Col xs={12}>{this.renderPropsFilesRow(files, onDelete, canOpenFiles)}</Col>
        </Row>
      </>
    ) : null
  }
}

export default injectIntl(Fileupload)
